/**
 * Realizzazione — handoff disegno tecnico → foto reale (Figma, stile SpaceX).
 *
 * Lo sticky dei due layer (disegno + foto) è CSS puro (_realizzazione.scss): la
 * foto, ritardata, sale dal basso finché non si sovrappone al disegno appeso in
 * cima. Le due immagini hanno la stessa dimensione, quindi la sovrapposizione
 * completa si rileva confrontando i bordi superiori (uguali → una sopra l'altra).
 *
 * Al momento della sovrapposizione completa parte una SEQUENZA A TEMPO (non
 * legata allo scroll): il glow sulle linee appare subito e, dopo ~2s, il disegno
 * si dissolve scoprendo la foto. Tornando a separarle, la sequenza si resetta.
 *
 * Attivo solo su desktop (≥992px) senza prefers-reduced-motion; altrimenti i due
 * layer restano impilati in flow (fallback statico via CSS).
 *
 * Markup: template-parts/realizzazioni/drawing.php ([data-drawing-sticky] /
 * [data-drawing-layer] / [data-drawing-photo] / [data-drawing-glow]).
 */
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

( function () {
	'use strict';

	const section = document.querySelector( '[data-drawing-sticky]' );
	if ( ! section ) {
		return;
	}

	const photo = section.querySelector( '[data-drawing-photo]' );
	const drawing = section.querySelector( '[data-drawing-layer]' );
	if ( ! photo || ! drawing ) {
		return; // foto assente: layer impilati statici, nessun handoff.
	}

	const glow = section.querySelector( '[data-drawing-glow]' );

	gsap.registerPlugin( ScrollTrigger );

	const mm = gsap.matchMedia();

	mm.add( '(min-width: 992px) and (prefers-reduced-motion: no-preference)', () => {
		section.classList.add( 'is-crossfade' );

		const OVERLAP_EPS = 2;   // px: tolleranza per "perfettamente sovrapposte"
		const HOLD = 2;          // s: attesa col glow acceso prima della dissolvenza

		gsap.set( drawing, { opacity: 1 } );
		if ( glow ) {
			gsap.set( glow, { opacity: 0, scale: 1 } );
		}

		// Sequenza a tempo, avviata quando le due immagini si sovrappongono del
		// tutto: il glow appare subito, resta acceso ~2s, poi il disegno (glow
		// incluso, è un figlio del layer) si dissolve scoprendo la foto.
		const tl = gsap.timeline( { paused: true } );
		if ( glow ) {
			tl.to( glow, { opacity: 0.85, scale: 1.03, duration: 0.5, ease: 'power2.out' }, 0 );
		}
		tl.to( drawing, { opacity: 0, duration: 0.8, ease: 'power1.inOut' }, HOLD );

		// Stessa dimensione → sono "una sopra l'altra" quando i bordi superiori
		// coincidono (entrambe sticky a top:120). Prima di allora la foto, che
		// sale dal basso, ha un top maggiore del disegno.
		let overlapping = false;
		const update = () => {
			const gap = Math.abs(
				photo.getBoundingClientRect().top - drawing.getBoundingClientRect().top
			);
			const now = gap <= OVERLAP_EPS;
			if ( now === overlapping ) {
				return;
			}
			overlapping = now;
			if ( now ) {
				tl.play(); // sovrapposizione completa → glow, poi (2s) dissolvenza
			} else {
				tl.pause( 0 ); // di nuovo separate → reset istantaneo (disegno pieno)
			}
		};

		const st = ScrollTrigger.create( {
			trigger: section,
			start: 'top bottom',
			end: 'bottom top',
			onUpdate: update,
			onRefresh: update,
			invalidateOnRefresh: true,
		} );
		update();

		// Cleanup automatico al revert (viewport < 992px o reduced-motion).
		return () => {
			st.kill();
			tl.kill();
			gsap.set( drawing, { clearProps: 'opacity' } );
			if ( glow ) {
				gsap.set( glow, { clearProps: 'opacity,transform' } );
			}
			section.classList.remove( 'is-crossfade' );
		};
	} );

	// Le immagini lazy caricate dopo l'init cambiano le altezze di colonna:
	// ricalcolo delle posizioni a pagina completamente caricata.
	window.addEventListener( 'load', () => {
		ScrollTrigger.refresh();
	} );
}() );
